import React, { useRef, useEffect, KeyboardEvent, ClipboardEvent, ChangeEvent } from "react";
import {
  Box,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import colors from "../../../shared/themes/colors";
import { handlePinLogin, clearPinError } from "../../loginSlice";
import useTranslation from "../../../shared/hooks/useTranslation";
import { PinLogin } from "../../models/AccountModels";
import { localStorageUtils, STORAGE_KEYS } from "../../../shared/utils/localStorage";
import { dt } from "../../../shared/themes/redesignTokens";

interface PinLoginFormProps {
  onSubmitSuccess?: () => void;
  formId?: string;
  className?: string;
}

const PinLoginForm = ({
  onSubmitSuccess,
  formId = "pinLoginForm",
  className = "",
}: PinLoginFormProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const dispatch = useAppDispatch();
  const { translate } = useTranslation("account");

  const {
    isLoading, isError
  } = useAppSelector((state) => state.login);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const {
    handleSubmit,
    control,
    watch,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm<PinLogin>({
    mode: "onSubmit",
    defaultValues: {
      pinCode: ""
    }
  });

  useEffect(() => {
    if (isError) {
      const savedPinCode = localStorageUtils.getItem(STORAGE_KEYS.PIN_CODE);
      if (savedPinCode) {
        setValue('pinCode', savedPinCode);
      }
    }
  }, [isError, setValue]);

  const watchedPinCode = watch("pinCode");
  const isFormValid = watchedPinCode && watchedPinCode.length === 6;

  const handlePinChange = (onChange: (value: string) => void, currentValue: string, index: number, inputValue: string) => {
    const numericValue = inputValue.replace(/\D/g, "");

    if (numericValue.length <= 1) {
      const newPinCode = currentValue.split("");
      newPinCode[index] = numericValue;
      const newValue = newPinCode.join("");
      onChange(newValue);

      if (errors.pinCode) {
        clearErrors("pinCode");
      }
      if (isError) {
        dispatch(clearPinError());
      }

      if (numericValue && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, currentValue: string, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !currentValue[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (event.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (onChange: (value: string) => void, event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData("text").replace(/\D/g, "");

    if (pastedData.length <= 6) {
      const newPinCode = ["", "", "", "", "", ""];
      for (let i = 0; i < pastedData.length; i++) {
        newPinCode[i] = pastedData[i];
      }
      const newValue = newPinCode.join("");
      onChange(newValue);

      if (errors.pinCode) {
        clearErrors("pinCode");
      }
      if (isError) {
        dispatch(clearPinError());
      }

      const lastFilledIndex = Math.min(pastedData.length - 1, 5);
      inputRefs.current[lastFilledIndex]?.focus();
    }
  };

  const onSubmit: SubmitHandler<PinLogin> = async (data: PinLogin) => {
    localStorageUtils.setItem(STORAGE_KEYS.PIN_CODE, data.pinCode);

    const result = await dispatch(handlePinLogin(data));

    if (handlePinLogin.fulfilled.match(result)) {
      onSubmitSuccess?.();
    }
  };

  return (
    <Box className={className}>
      <Box component="form" id={formId} onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="pinCode"
          control={control}
          rules={{
            required: true,
            validate: (value: string) => value.length === 6 || translate("pinCodeInvalid6Digits")
          }}
          render={({ field: { onChange, value } }) => (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                gap: isMobile ? "8px" : "12px",
                justifyContent: "center",
                mt: "20px",
                p: isMobile ? "0px" : "20px",
              }}
            >
              {Array.from({ length: 6 }, (_, index: number) => (
                <Box
                  key={index}
                  component="input"
                  ref={(el: HTMLInputElement) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={value[index] || ""}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handlePinChange(onChange, value, index, e.target.value)
                  }
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
                    handleKeyDown(index, value, e)
                  }
                  onPaste={(e: ClipboardEvent<HTMLInputElement>) =>
                    handlePaste(onChange, e)
                  }
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  sx={{
                    width: isMobile ? "42px" : "60px",
                    height: isMobile ? "42px" : "60px",
                    border: `1px solid ${(errors.pinCode || isError)
                      ? colors.primary.red
                      : value[index]
                        ? dt.blue
                        : colors.secondary.gray80
                      }`,
                    borderRadius: "4px",
                    textAlign: "center",
                    fontSize: isMobile ? "18px" : "24px",
                    fontWeight: "500",
                    outline: "none",
                    transition: "border-color 0.2s ease-in-out",
                    backgroundColor: "transparent",
                    color: "inherit",
                    fontFamily: "inherit",

                    "&:hover": {
                      borderColor: (errors.pinCode || isError)
                        ? colors.primary.red
                        : dt.blue,
                    },

                    "&:focus": {
                      borderColor: (errors.pinCode || isError)
                        ? colors.primary.red
                        : dt.blue,
                      borderWidth: "2px",
                    },

                    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                    "&[type=number]": {
                      MozAppearance: "textfield",
                    },
                  }}
                />
              ))}
            </Box>
          )}
        />

        {(errors.pinCode || isError) && (
          <Box
            sx={{
              color: colors.primary.red,
              fontSize: "14px",
              textAlign: "center",
              mt: 1,
              px: 2,
            }}
          >
            {errors.pinCode ? translate("pinCodeInvalid6Digits") : translate("pinCodeInvalid")}
          </Box>
        )}

        <Box sx={{ width: "100%", padding: 0, mt: "40px" }}>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={!isFormValid || isLoading}
          >
            {translate("logIn")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PinLoginForm;